import React, { useContext, useState, useEffect } from "react";
import useBreadcrumbs from "../../utils/hooks/useBreadcrumbs";
import { FaShoppingCart } from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import Alert from "../../UI/Alert/Alert";
import { NotifyContext } from "../../context/notifyContext";
import OrderService from "../../services/api/OrderService";
import { StripeOrderDetails } from "../../Types/Dashboard";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

export default function OrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useContext(NotifyContext);

  useBreadcrumbs([
    {
      label: "Commandes",
      icon: <FaShoppingCart />,
      is_link: true,
      href: "/app/orders",
    },
    {
      label: id ?? "",
      is_link: false,
    },
    {
      label: "Détails",
      is_link: false,
    },
  ]);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<StripeOrderDetails | null>(null);

  useEffect(() => {
    if (id) {
      fetchOrderDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await OrderService.getStripeOrderDetails(Number(id));
      setOrder(response.data.order);
    } catch (err: any) {
      setError(err?.message || "Erreur lors du chargement de la commande");
      notify(err?.message || "Erreur lors du chargement de la commande", "error");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "dd MMM yyyy, HH:mm", { locale: fr });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "paid":
      case "complete":
      case "completed":
      case "delivered":
        return "bg-green-100 text-green-800";
      case "pending":
      case "unpaid":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
      case "failed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center w-full p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="p-6">
        <Alert color="error">{error || "Commande non trouvée"}</Alert>
        <button
          onClick={() => navigate("/app/orders")}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Commande #{order.id}</h1>
          <span
            className={`px-3 py-1 rounded text-sm font-medium ${getStatusColor(
              order.status
            )}`}
          >
            {order.status}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h2 className="text-lg font-semibold mb-4">Informations générales</h2>
            <div className="space-y-3">
              <div>
                <span className="text-gray-600">Date de commande:</span>
                <p className="font-medium">{formatDate(order.created_at)}</p>
              </div>
              {order.stripe_session_id && (
                <div>
                  <span className="text-gray-600">Session Stripe:</span>
                  <p className="font-medium text-sm">{order.stripe_session_id}</p>
                </div>
              )}
              <div>
                <span className="text-gray-600">Montant total:</span>
                <p className="font-medium text-lg">
                  {order.amount.toLocaleString()} {order.currency}
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-4">Client</h2>
            <div className="space-y-3">
              {order.customer?.id && (
                <div>
                  <span className="text-gray-600">ID Stripe:</span>
                  <p className="font-medium text-sm">{order.customer.id}</p>
                </div>
              )}
              <div>
                <span className="text-gray-600">Nom:</span>
                <p className="font-medium">
                  {order.customer?.name || order.customer_name || "-"}
                </p>
              </div>
              {(order.customer?.email || order.customer_email) && (
                <div>
                  <span className="text-gray-600">Email:</span>
                  <p className="font-medium">{order.customer?.email || order.customer_email}</p>
                </div>
              )}
              {order.customer?.phone && (
                <div>
                  <span className="text-gray-600">Téléphone:</span>
                  <p className="font-medium">{order.customer.phone}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Produits */}
      {order.products && order.products.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Produits commandés</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantité
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prix unitaire
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {order.products.map((product, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{product.name}</div>
                      {product.description && (
                        <div className="text-xs text-gray-500">{product.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.quantity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.unit_price.toLocaleString()} {product.currency}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {product.total_price.toLocaleString()} {product.currency}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                    Total:
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    {order.amount.toLocaleString()} {order.currency}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Adresse de livraison */}
      {order.address && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Adresse de livraison</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-gray-600">Nom complet:</span>
              <p className="font-medium">{order.address.fullname || "-"}</p>
            </div>
            <div>
              <span className="text-gray-600">Email:</span>
              <p className="font-medium">{order.address.email || "-"}</p>
            </div>
            <div>
              <span className="text-gray-600">Téléphone:</span>
              <p className="font-medium">{order.address.phone || "-"}</p>
            </div>
            <div>
              <span className="text-gray-600">Ville:</span>
              <p className="font-medium">{order.address.city || "-"}</p>
            </div>
            <div className="md:col-span-2">
              <span className="text-gray-600">Adresse complète:</span>
              <p className="font-medium">{order.address.address_details || "-"}</p>
            </div>
            <div>
              <span className="text-gray-600">Code postal:</span>
              <p className="font-medium">{order.address.postal_code || "-"}</p>
            </div>
            <div>
              <span className="text-gray-600">Pays:</span>
              <p className="font-medium">{order.address.country || "-"}</p>
            </div>
          </div>
        </div>
      )}


      <div className="flex gap-4">
        <button
          onClick={() => navigate("/app/orders")}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
        >
          Retour à la liste
        </button>
      </div>
    </div>
  );
}
